import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Chip, IconButton, Tooltip } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ClearIcon from '@material-ui/icons/Clear';
import PeopleIcon from '@material-ui/icons/People';
import { getOverlapVector, getThesisIntegrityData } from './thesisIntegrityUtils';
import ScoreMethodologyTooltip from './ScoreMethodologyTooltip';
import ActionBadge from './ActionBadge';

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.26)',
    background: 'rgba(10, 14, 30, 0.92)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    cursor: 'pointer',
    transition: 'transform 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      borderColor: 'rgba(77, 121, 255, 0.55)',
      boxShadow: '0 0 18px rgba(77,121,255,0.12)',
    },
  },
  selected: {
    borderColor: 'rgba(158, 247, 197, 0.55)',
    boxShadow: '0 0 20px rgba(158,247,197,0.12)',
  },
  top: {
    padding: '12px 12px 10px',
    background: 'linear-gradient(90deg, rgba(77,121,255,0.22), rgba(10,14,30,0.96))',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  dirChip: {
    height: 20,
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 900,
    color: '#fff',
  },
  convBarOuter: {
    flex: 1,
    minWidth: 90,
    height: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.10)',
    overflow: 'hidden',
  },
  convBarInner: {
    height: '100%',
    borderRadius: 999,
  },
  metaText: {
    fontSize: 11,
    color: '#9ea4d8',
    whiteSpace: 'nowrap',
  },
  body: {
    padding: '10px 12px 12px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  entity: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: -0.1,
    color: '#e5e7ff',
  },
  price: {
    fontSize: 11,
    color: '#cfd8ff',
  },
  thesisText: {
    fontSize: 12,
    lineHeight: 1.55,
    color: '#e8f5ff',
    opacity: 0.92,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  bottom: {
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  chipsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  storyTypeChip: {
    height: 20,
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    backgroundColor: 'rgba(77, 121, 255, 0.15)',
    border: '1px solid rgba(77, 121, 255, 0.45)',
    color: '#ffe55c',
    fontWeight: 800,
  },
  annotationIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    color: '#9ea4d8',
  },
  overlapGlyph: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  overlapDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    backgroundColor: '#9ef7c5',
    border: '1px solid rgba(158, 247, 197, 0.35)',
  },
  methodologyDrawer: {
    marginTop: 10,
    padding: 10,
    borderRadius: 10,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(5, 8, 20, 0.68)',
  },
  methodologyTitle: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.45,
    textTransform: 'uppercase',
    color: '#e5e7ff',
    marginBottom: 6,
  },
  methodologyMeta: {
    fontSize: 11,
    color: '#cfd8ff',
    lineHeight: 1.5,
  },
  methodologyEquation: {
    marginTop: 6,
    fontSize: 12,
    color: '#9ec4ff',
    fontFamily: '"SFMono-Regular", Consolas, monospace',
  },
  methodologyBars: {
    marginTop: 8,
    display: 'grid',
    gap: 6,
  },
  methodologyBarRow: {
    display: 'grid',
    gridTemplateColumns: '52px minmax(0, 1fr) 44px',
    alignItems: 'center',
    gap: 8,
  },
  methodologyBarTrack: {
    height: 6,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  methodologyBarFill: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, rgba(77,121,255,0.55), rgba(158,196,255,0.95))',
  },
}));

function dirColors(direction) {
  const d = String(direction || '').toUpperCase();
  if (d === 'LONG') return { bg: '#2e7d32', label: 'LONG' };
  if (d === 'SHORT') return { bg: '#c62828', label: 'SHORT' };
  if (d === 'AVOID') return { bg: '#616161', label: 'AVOID' };
  return { bg: 'rgba(255,255,255,0.18)', label: d || '—' };
}

function convColor(conviction) {
  const c = Number(conviction);
  if (!Number.isFinite(c)) return '#9e9e9e';
  if (c >= 0.7) return '#2e7d32';
  if (c >= 0.4) return '#ffb300';
  return '#9e9e9e';
}

function formatUsd(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return '—';
  if (x >= 1e9) return `$${(x / 1e9).toFixed(2)}B`;
  if (x >= 1e6) return `$${(x / 1e6).toFixed(2)}M`;
  if (x >= 1e3) return `$${(x / 1e3).toFixed(2)}K`;
  if (x >= 1) return `$${x.toFixed(4)}`;
  return `$${x.toPrecision(3)}`;
}

function shortenAddressesInText(text) {
  const s = String(text || '');
  if (!s) return '';
  return s.replace(/0x[0-9a-fA-F]{40}/g, (m) => `${m.slice(0, 6)}...${m.slice(-4)}`);
}

export default function ThesisCard({ thesis, annotation, isSelected, onClick, isPremiumPlus, openActionType = null, showGroupToggle = false, isGrouped = false, onToggleGroup = null }) {
  const classes = useStyles();
  const [showMethodology, setShowMethodology] = useState(false);

  const direction = String(thesis && thesis.direction ? thesis.direction : '').toUpperCase();
  const dir = dirColors(direction);
  const conviction = Number(thesis && thesis.conviction != null ? thesis.conviction : 0);
  const horizon = thesis && thesis.time_horizon ? String(thesis.time_horizon) : '';

  const entityLabel = useMemo(() => {
    const sym = thesis && thesis.entity_symbol ? String(thesis.entity_symbol).trim() : '';
    const name = thesis && thesis.entity_name ? String(thesis.entity_name).trim() : '';
    const addr = thesis && thesis.entity_address ? String(thesis.entity_address) : '';
    if (sym) return name ? `${sym} (${name})` : sym;
    if (name) return name;
    return addr ? addr : '—';
  }, [thesis]);

  const storyTypes = useMemo(() => {
    const raw = thesis && Array.isArray(thesis.story_types) ? thesis.story_types : [];
    const uniq = Array.from(new Set(raw.map((s) => String(s || '').trim()).filter(Boolean)));
    if (uniq.length) return uniq.slice(0, 6);
    return ['SIGNAL'];
  }, [thesis]);

  const annotStatus = annotation && annotation.userStatus ? String(annotation.userStatus) : 'none';
  const annotIcon = (() => {
    if (annotStatus === 'watching') return <VisibilityIcon style={{ fontSize: 16, color: '#5aa7ff' }} />;
    if (annotStatus === 'acted_on') return <CheckCircleIcon style={{ fontSize: 16, color: '#2e7d32' }} />;
    if (annotStatus === 'passed') return <ClearIcon style={{ fontSize: 16, color: '#9e9e9e' }} />;
    return null;
  })();

  const signalCount = thesis && Array.isArray(thesis.contributing_story_ids) ? thesis.contributing_story_ids.length : 0;
  const riskCount = thesis && Array.isArray(thesis.risk_factors) ? thesis.risk_factors.length : 0;
  const overlapVector = useMemo(() => getOverlapVector(thesis), [thesis]);
  const integrity = useMemo(() => getThesisIntegrityData(thesis), [thesis]);
  const overlapTooltip = overlapVector.map((item) => `${item.label}: ${Math.round(item.value * 100)}%`).join(' · ');
  const machineCheckableInvalidations = thesis && Array.isArray(thesis.invalidation_predicates)
    ? thesis.invalidation_predicates.length
    : 0;

  return (
    <div
      className={`${classes.card} ${isSelected ? classes.selected : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick && onClick();
        }
      }}
    >
      <div className={classes.top}>
        <div className={classes.row}>
          <Chip
            size="small"
            label={dir.label}
            className={classes.dirChip}
            style={{ backgroundColor: dir.bg }}
          />

          <div className={classes.convBarOuter}>
            <div
              className={classes.convBarInner}
              style={{ width: `${Math.max(0, Math.min(1, conviction)) * 100}%`, backgroundColor: convColor(conviction) }}
            />
          </div>

          <Typography className={classes.metaText}>{horizon || '—'}</Typography>

          <ScoreMethodologyTooltip
            glyph="sqrt"
            ariaLabel="How this thesis was measured"
            onActivate={(e) => {
              try {
                if (e && typeof e.preventDefault === 'function') e.preventDefault();
                if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
              } catch (err) {
              }
              setShowMethodology((value) => !value);
            }}
            buttonStyle={{ padding: 4, color: showMethodology ? '#9ef7c5' : '#9ea4d8' }}
          />

          {showGroupToggle ? (
            <IconButton
              size="small"
              title={isGrouped ? 'Remove from group evidence' : 'Add to group evidence'}
              onClick={(e) => {
                try {
                  if (e && typeof e.preventDefault === 'function') e.preventDefault();
                  if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                } catch (err) {
                }
                if (typeof onToggleGroup === 'function') onToggleGroup(e);
              }}
              style={{ padding: 4, color: isGrouped ? '#9ef7c5' : '#9ea4d8', marginLeft: 'auto' }}
            >
              <PeopleIcon style={{ fontSize: 16 }} />
            </IconButton>
          ) : null}
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.titleRow}>
          <Typography className={classes.entity}>{entityLabel}</Typography>
          <Typography className={classes.price}>{`Entry: ${formatUsd(thesis && thesis.price_at_creation)}`}</Typography>
        </div>

        <div className={classes.thesisText}>{thesis && thesis.thesis_text ? shortenAddressesInText(thesis.thesis_text) : ''}</div>

        <div className={classes.bottom}>
          <div className={classes.annotationIcon}>
            {annotIcon}
            <span>{annotStatus !== 'none' ? annotStatus : ''}</span>
            {isPremiumPlus && (annotation && (annotation.entryPrice != null || annotation.positionSize)) ? (
              <span style={{ color: '#cfd8ff' }}>tracked</span>
            ) : null}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Tooltip title={`Convergence overlap vector. ${overlapTooltip || 'No overlap yet.'}`}>
              <div className={classes.overlapGlyph} aria-label="Convergence overlap vector">
                {overlapVector.map((item) => (
                  <span
                    key={item.key}
                    className={classes.overlapDot}
                    style={{ opacity: Math.max(0.2, item.value) }}
                  />
                ))}
              </div>
            </Tooltip>
            <Typography className={classes.metaText}>{`📊 ${signalCount} signals  ⚠️ ${riskCount} risks`}</Typography>
          </div>
        </div>

        <div className={classes.chipsRow} style={{ marginTop: 10 }}>
          {storyTypes.map((t) => (
            <Chip key={t} size="small" label={t} className={classes.storyTypeChip} />
          ))}
          {openActionType ? <ActionBadge actionType={openActionType} /> : null}
        </div>

        {showMethodology ? (
          <div className={classes.methodologyDrawer}>
            <div className={classes.methodologyTitle}>How This Was Measured</div>
            <div className={classes.methodologyMeta}>
              {`Conviction ${conviction.toFixed(2)} reflects overlap, diversity, support depth, history strength, and risk pressure under OTA.`}
            </div>
            <div className={classes.methodologyEquation}>
              {`C^Theta_t = 0.25·Q_S + 0.25·O + 0.15·D + 0.15·H + 0.10·X − 0.10·R`}
            </div>
            {integrity ? (
              <div className={classes.methodologyBars}>
                {integrity.components.map((item) => (
                  <div key={item.key} className={classes.methodologyBarRow}>
                    <div className={classes.methodologyMeta}>{item.label}</div>
                    <div className={classes.methodologyBarTrack}>
                      <div className={classes.methodologyBarFill} style={{ width: `${Math.max(0, Math.min(1, item.value)) * 100}%` }} />
                    </div>
                    <div className={classes.methodologyMeta} style={{ textAlign: 'right' }}>
                      {item.value.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            <div className={classes.methodologyMeta} style={{ marginTop: 8 }}>
              {`Overlap ${Number((thesis && thesis.o_total) || 0).toFixed(2)} · ${machineCheckableInvalidations} machine-checkable invalidation predicate${machineCheckableInvalidations === 1 ? '' : 's'}.`}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
